import React, { useState } from "react";
import api from "../../api/api";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
} from "@mui/material";

import EmailIcon from "@mui/icons-material/Email";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleRequest = async (e) => {
    e.preventDefault();

    setLoading(true);
    setMessage("");

    setTimeout(async () => {
      try {
        const res = await api.post("/request-reset-code/", {
          email,
        });

        setMessage(res.data.message || "Verification code sent.");

        navigate("/verifyCode", {
          state: { email },
        });
      } catch (err) {
        setMessage(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Something went wrong."
        );
      } finally {
        setLoading(false);
      }
    }, 2000);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "#ffffff",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,

        "@keyframes float1": {
          "0%": {
            transform: "translateY(0px)",
          },
          "50%": {
            transform: "translateY(-35px)",
          },
          "100%": {
            transform: "translateY(0px)",
          },
        },

        "@keyframes float2": {
          "0%": {
            transform: "translateY(0px) translateX(0px)",
          },
          "50%": {
            transform: "translateY(25px) translateX(-20px)",
          },
          "100%": {
            transform: "translateY(0px) translateX(0px)",
          },
        },

        "@keyframes float3": {
          "0%": {
            transform: "translateY(0px) translateX(0px)",
          },
          "50%": {
            transform: "translateY(-20px) translateX(20px)",
          },
          "100%": {
            transform: "translateY(0px) translateX(0px)",
          },
        },

        "@keyframes twinkle": {
          "0%": {
            opacity: 0.2,
          },
          "50%": {
            opacity: 1,
          },
          "100%": {
            opacity: 0.2,
          },
        },
      }}
    >
      {/* ===================== LARGE BUBBLES ===================== */}

      <Box
        sx={{
          position: "absolute",
          width: { xs: 180, md: 320 },
          height: { xs: 180, md: 320 },
          borderRadius: "50%",
          background: "rgba(255,140,0,.14)",
          top: "5%",
          left: "-5%",
          animation: "float1 14s ease-in-out infinite",
          filter: "blur(5px)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 200, md: 350 },
          height: { xs: 200, md: 350 },
          borderRadius: "50%",
          background: "rgba(33,150,243,.12)",
          right: "-8%",
          top: "55%",
          animation: "float2 18s ease-in-out infinite",
          filter: "blur(5px)",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 170, md: 280 },
          height: { xs: 170, md: 280 },
          borderRadius: "50%",
          background: "rgba(255,183,77,.18)",
          left: "25%",
          bottom: "-5%",
          animation: "float3 20s ease-in-out infinite",
          filter: "blur(5px)",
        }}
      />

      {/* ===================== MEDIUM BUBBLES ===================== */}

      <Box
        sx={{
          position: "absolute",
          width: { xs: 90, md: 130 },
          height: { xs: 90, md: 130 },
          borderRadius: "50%",
          background: "rgba(144,164,174,.15)",
          top: "15%",
          right: "15%",
          animation: "float2 12s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 80, md: 110 },
          height: { xs: 80, md: 110 },
          borderRadius: "50%",
          background: "rgba(255,140,0,.12)",
          bottom: "15%",
          left: "10%",
          animation: "float1 16s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 100, md: 140 },
          height: { xs: 100, md: 140 },
          borderRadius: "50%",
          background: "rgba(33,150,243,.12)",
          top: "42%",
          left: "8%",
          animation: "float3 15s ease-in-out infinite",
        }}
      />

      {/* ===================== STARS ===================== */}

      {[...Array(40)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: `${2 + (i % 3)}px`,
            height: `${2 + (i % 3)}px`,
            borderRadius: "50%",
            background:
              i % 4 === 0
                ? "#ff8c00"
                : i % 4 === 1
                ? "#2196f3"
                : i % 4 === 2
                ? "#ffb74d"
                : "#90a4ae",
            top: `${(i * 13) % 100}%`,
            left: `${(i * 17) % 100}%`,
            opacity: 0.6,
            animation: `twinkle ${3 + (i % 4)}s infinite`,
          }}
        />
      ))}

      {/* ===================== CARD ===================== */}

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.8,
        }}
        style={{
          width: "100%",
          maxWidth: "520px",
          zIndex: 5,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            p: {
              xs: 3,
              sm: 4,
              md: 5,
            },

            borderRadius: "30px",

            background: "rgba(255,255,255,.92)",

            backdropFilter: "blur(20px)",

            border: "1px solid rgba(255,140,0,.12)",

            boxShadow:
              "0 20px 60px rgba(0,0,0,.08)",
          }}
        >
          {/* HEADER */}

          <Box textAlign="center">
            <motion.div
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                repeat: Infinity,
                duration: 2.5,
              }}
            >
              <LocalShippingIcon
                sx={{
                  fontSize: {
                    xs: 60,
                    md: 75,
                  },
                  color: "#ff8c00",
                }}
              />
            </motion.div>

            <Typography
              variant="h4"
              fontWeight="800"
              sx={{
                mt: 2,
                color: "#111",
                fontSize: {
                  xs: "1.9rem",
                  md: "2.3rem",
                },
              }}
            >
              Forgot Password
            </Typography>

            <Typography
              sx={{
                mt: 1,
                color: "#666",
                maxWidth: "380px",
                mx: "auto",
                fontSize: {
                  xs: ".95rem",
                  md: "1rem",
                },
              }}
            >
              Securely recover access to your Zuluwhiskey Shipping
              account. We'll send a verification code to your email.
            </Typography>
          </Box>

          {/* FORM */}

          <Box
            component="form"
            onSubmit={handleRequest}
            sx={{
              mt: 4,
            }}
          >
            <TextField
              fullWidth
              required
              type="email"
              label="Email Address"
              value={email}
              disabled={loading}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon
                      sx={{
                        color: "#ff8c00",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,

                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                },
              }}
            />

            <Button
              fullWidth
              type="submit"
              disabled={loading}
              sx={{
                py: 1.7,
                borderRadius: "14px",
                fontSize: "1rem",
                fontWeight: 700,

                background:
                  "linear-gradient(135deg,#ff8c00,#ffb74d)",

                color: "#fff",

                "&:hover": {
                  background:
                    "linear-gradient(135deg,#ff9800,#ff8c00)",
                },
              }}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "#fff",
                  }}
                />
              ) : (
                "Send Verification Code"
              )}
            </Button>

            <Button
              fullWidth
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/login")}
              sx={{
                mt: 2,
                color: "#555",
                fontWeight: 600,
              }}
            >
              Back to Login
            </Button>
          </Box>

          {message && (
            <Typography
              textAlign="center"
              mt={3}
              sx={{
                color: "#ff8c00",
                fontWeight: 600,
              }}
            >
              {message}
            </Typography>
          )}

          {/* FOOTER */}

          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #eee",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                color: "#777",
                fontSize: ".9rem",
              }}
            >
              🚢 Fast • Secure • Reliable Shipping Solutions
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default ForgotPassword;
